// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { ROUTES } from '../router/const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { FAQ_TOPICS } from '../services/consts/faq/topics.const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { FAQ_ARTICLES } from '../services/consts/faq/articles.const.js'

export const BASE_DOMAIN = process.env.VITE_BASE_DOMAIN || process.env.BASE_DOMAIN

// Open Graph / Twitter share image (`public/og-image.png`).
export const THUMBNAIL_PATH = '/og-image.png'
export const THUMBNAIL_WIDTH = 1024
export const THUMBNAIL_HEIGHT = 569

export const DEFAULT_META = {
  title: 'CookieArmor',
  description:
    'CookieArmor helps teams ship compliant cookie consent fast, with lightweight banners, automatic regional behavior, cookie scanning, and audit-ready records.',
  image: `${BASE_DOMAIN}${THUMBNAIL_PATH}`,
  imageWidth: String(THUMBNAIL_WIDTH),
  imageHeight: String(THUMBNAIL_HEIGHT),
  url: BASE_DOMAIN,
}

/** Append `| CookieArmor` unless the title already includes the brand. */
export function withSiteTitle(title) {
  const brand = DEFAULT_META.title
  if (!title || title === brand) return brand
  if (title.endsWith(` | ${brand}`)) return title
  return `${title} | ${brand}`
}

// ============================================================================
// ROUTES METADATA
// ============================================================================
// For route meta config
// Labels and keys mirror BANNER_DETAIL_TABS in banner/detail.const.js — kept local because
// this file is also loaded by scripts/generate-static-meta.js (Node cannot resolve src/ aliases).
const BANNER_DETAIL_TAB_LABELS = {
  [ROUTES.BANNER_DETAIL_OVERVIEW]: 'Overview',
  [ROUTES.BANNER_DETAIL_COOKIES_TRACKERS]: 'Cookies & Trackers',
  [ROUTES.BANNER_DETAIL_APPEARANCE]: 'Appearance',
  [ROUTES.BANNER_DETAIL_BEHAVIOUR]: 'Behaviour',
}

const BANNER_DETAIL_TAB_KEYS = {
  [ROUTES.BANNER_DETAIL_OVERVIEW]: 'overview',
  [ROUTES.BANNER_DETAIL_COOKIES_TRACKERS]: 'cookies-trackers',
  [ROUTES.BANNER_DETAIL_APPEARANCE]: 'appearance',
  [ROUTES.BANNER_DETAIL_BEHAVIOUR]: 'behaviour',
}

// Labels and URL segments mirror SETTINGS_TABS in settings.const.js — kept local because
// this file is also loaded by scripts/generate-static-meta.js (Node cannot resolve src/ aliases).
const SETTINGS_TAB_LABELS = {
  [ROUTES.SETTINGS_ACCOUNT_DETAILS]: 'Account Details',
  [ROUTES.SETTINGS_ACCOUNT_MEMBERS]: 'Account Members',
  [ROUTES.SETTINGS_SYSTEM_SETTINGS]: 'System Settings',
}

const SETTINGS_TAB_URLS = {
  [ROUTES.SETTINGS_ACCOUNT_DETAILS]: 'account-details',
  [ROUTES.SETTINGS_ACCOUNT_MEMBERS]: 'account-members',
  [ROUTES.SETTINGS_SYSTEM_SETTINGS]: 'system-settings',
}

const BANNER_DETAIL_DESCRIPTIONS = {
  [ROUTES.BANNER_DETAIL_OVERVIEW]:
    'See what is live, what needs attention, and how your banner is protecting consent compliance.',
  [ROUTES.BANNER_DETAIL_COOKIES_TRACKERS]:
    'Discover what is running on your site, classify cookies and trackers, and keep your consent inventory current.',
  [ROUTES.BANNER_DETAIL_APPEARANCE]:
    'Make your banner feel native to your brand while keeping consent choices clear for every visitor.',
  [ROUTES.BANNER_DETAIL_BEHAVIOUR]:
    'Control how consent works by region, regulation, blocking rules, and visitor choice.',
}

// Labels mirror CREATE_BANNER_STEPS in banner/create.const.js — kept local because
// this file is also loaded by scripts/generate-static-meta.js (Node cannot resolve src/ aliases).
const CREATE_BANNER_STEP_LABELS = {
  [ROUTES.CREATE_BANNER_REGULATIONS]: 'Regulations',
  [ROUTES.CREATE_BANNER_DETAILS]: 'Details',
  [ROUTES.CREATE_BANNER_CONFIGURE]: 'Configure',
  [ROUTES.CREATE_BANNER_CREATED]: 'Done',
}

const CREATE_BANNER_DESCRIPTION =
  'Launch a compliant cookie banner in minutes with guided setup for regulations, branding, behavior, and install.'

const BANNER_DETAIL_DYNAMIC_DESCRIPTIONS = {
  [ROUTES.BANNER_DETAIL_OVERVIEW]:
    'Review setup progress, installation status, scans, and compliance actions for',
  [ROUTES.BANNER_DETAIL_COOKIES_TRACKERS]:
    'Discover cookies and trackers, review scan history, and keep consent categories audit-ready for',
  [ROUTES.BANNER_DETAIL_APPEARANCE]:
    'Tune banner layout, theme, and visitor-facing presentation for',
  [ROUTES.BANNER_DETAIL_BEHAVIOUR]:
    'Manage regional rules, blocking behavior, and consent flows for',
}

/** Shared Help Center copy for all FAQ routes (landing, topic, and article). */
const FAQ_CENTER_NAME = 'Help Center'
const FAQ_CENTER_TITLE = withSiteTitle(FAQ_CENTER_NAME)
const FAQ_CENTER_DESCRIPTION =
  'Find answers for consent banners, cookie scans, script install, and staying compliant. Guides cover getting started, banner behaviour and appearance, developer setup, privacy basics, and managing your account and team.'

function createBannerDetailMeta(routeName) {
  const tabLabel = BANNER_DETAIL_TAB_LABELS[routeName]
  const tabKey = BANNER_DETAIL_TAB_KEYS[routeName]
  return {
    staticPath: `/banners/:siteId/${tabKey}`,
    meta: {
      title: `Banner Details | ${tabLabel}`,
      description: BANNER_DETAIL_DESCRIPTIONS[routeName],
    },
    getDynamicMeta(route, dynamicData) {
      const label = BANNER_DETAIL_TAB_LABELS[route.name] ?? 'Details'
      if (!dynamicData?.bannerName) return { title: `Banner Details | ${label}` }

      const descriptionPrefix = BANNER_DETAIL_DYNAMIC_DESCRIPTIONS[route.name]
      return {
        title: `${dynamicData.bannerName} | ${label}`,
        description: descriptionPrefix
          ? `${descriptionPrefix} ${dynamicData.bannerName}.`
          : undefined,
      }
    },
  }
}

/** Live-only step meta — no staticPath so generate-static-meta skips bot HTML. */
function createBannerStepMeta(routeName) {
  return {
    staticPath: null,
    meta: {
      title: `Create Banner | ${CREATE_BANNER_STEP_LABELS[routeName]}`,
      description: CREATE_BANNER_DESCRIPTION,
    },
  }
}

export const ROUTES_METADATA = {
  [ROUTES.OVERVIEW]: {
    staticPath: '/app',
    // `/` redirects to `/app` in the client only. Crawlers must get this same HTML.
    // Generator appends `/?$`, so this matches `/` and `/app`.
    rewritePattern: '(/app)?',
    meta: {
      title: withSiteTitle('Overview'),
      description:
        'Deploy a compliant cookie banner in minutes. Lightweight, fast to manage, and built to keep consent, scans, and audit trails under control.',
    },
  },
  [ROUTES.BANNERS]: {
    staticPath: `/${ROUTES.BANNERS}`,
    meta: {
      title: withSiteTitle('Banners'),
      description:
        'Create and manage lightweight consent banners that respect visitor choice, regional rules, and your site performance.',
    },
  },
  // Parent owns the single create-flow static/bot entry. URL segment is `create`, not ROUTES.CREATE_BANNER.
  // rewritePattern covers step deep-links (/regulations, /details, …) with the same bot HTML.
  [ROUTES.CREATE_BANNER]: {
    staticPath: `/${ROUTES.BANNERS}/create`,
    rewritePattern: `/${ROUTES.BANNERS}/create(/.*)?`,
    meta: {
      title: withSiteTitle('Create Banner'),
      description: CREATE_BANNER_DESCRIPTION,
    },
  },
  [ROUTES.CREATE_BANNER_REGULATIONS]: createBannerStepMeta(ROUTES.CREATE_BANNER_REGULATIONS),
  [ROUTES.CREATE_BANNER_DETAILS]: createBannerStepMeta(ROUTES.CREATE_BANNER_DETAILS),
  [ROUTES.CREATE_BANNER_CONFIGURE]: createBannerStepMeta(ROUTES.CREATE_BANNER_CONFIGURE),
  [ROUTES.CREATE_BANNER_CREATED]: createBannerStepMeta(ROUTES.CREATE_BANNER_CREATED),
  [ROUTES.BANNER_DETAIL_OVERVIEW]: createBannerDetailMeta(ROUTES.BANNER_DETAIL_OVERVIEW),
  [ROUTES.BANNER_DETAIL_COOKIES_TRACKERS]: createBannerDetailMeta(
    ROUTES.BANNER_DETAIL_COOKIES_TRACKERS
  ),
  [ROUTES.BANNER_DETAIL_APPEARANCE]: createBannerDetailMeta(ROUTES.BANNER_DETAIL_APPEARANCE),
  [ROUTES.BANNER_DETAIL_BEHAVIOUR]: createBannerDetailMeta(ROUTES.BANNER_DETAIL_BEHAVIOUR),
  // TODO(cookie-armor): replace placeholder description with real Cookie Armor analytics copy
  // once product/marketing copy is finalized.
  [ROUTES.ANALYTICS]: {
    staticPath: `/${ROUTES.ANALYTICS}`,
    meta: {
      title: withSiteTitle('Analytics'),
      description:
        'Understand consent performance, visitor choices, and compliance trends across your sites.',
    },
  },
  // Help Center — landing + nested routes need `staticPath` + bot files so crawlers (Slack, etc.) get OG HTML
  [ROUTES.FAQ]: {
    staticPath: '/faq',
    meta: {
      title: FAQ_CENTER_TITLE,
      description: FAQ_CENTER_DESCRIPTION,
    },
  },
  [ROUTES.FAQ_TOPIC]: {
    staticPath: '/faq/:topicId',
    /** Explicit filename — pathToBaseFilename would collide with `/faq` → `faq.html` */
    botMetadataFilename: 'faq-topic.html',
    meta: {
      title: FAQ_CENTER_TITLE,
      description: FAQ_CENTER_DESCRIPTION,
    },
    getDynamicMeta(route) {
      const topic = FAQ_TOPICS.find((entry) => entry.id === route.params?.topicId)
      return {
        title: `${topic?.title ?? 'Topic'} | ${FAQ_CENTER_NAME}`,
        description: FAQ_CENTER_DESCRIPTION,
      }
    },
  },
  [ROUTES.FAQ_ARTICLE]: {
    staticPath: '/faq/:topicId/:articleId',
    botMetadataFilename: 'faq-article.html',
    meta: {
      title: FAQ_CENTER_TITLE,
      description: FAQ_CENTER_DESCRIPTION,
    },
    getDynamicMeta(route) {
      const article = FAQ_ARTICLES[route.params?.articleId]
      return {
        title: `${article?.title ?? 'Article'} | ${FAQ_CENTER_NAME}`,
        description: FAQ_CENTER_DESCRIPTION,
      }
    },
  },
  [ROUTES.SETTINGS]: {
    staticPath: `/${ROUTES.SETTINGS}`,
    meta: {
      title: withSiteTitle('Settings'),
      description: 'Manage your CookieArmor account, team access, and workspace details.',
    },
  },
  [ROUTES.SETTINGS_ACCOUNT_DETAILS]: {
    staticPath: `/${ROUTES.SETTINGS}/${SETTINGS_TAB_URLS[ROUTES.SETTINGS_ACCOUNT_DETAILS]}`,
    meta: {
      title: withSiteTitle(SETTINGS_TAB_LABELS[ROUTES.SETTINGS_ACCOUNT_DETAILS]),
      description: 'Keep your account profile, identifiers, and workspace details up to date.',
    },
  },
  [ROUTES.SETTINGS_ACCOUNT_MEMBERS]: {
    staticPath: `/${ROUTES.SETTINGS}/${SETTINGS_TAB_URLS[ROUTES.SETTINGS_ACCOUNT_MEMBERS]}`,
    meta: {
      title: withSiteTitle(SETTINGS_TAB_LABELS[ROUTES.SETTINGS_ACCOUNT_MEMBERS]),
      description:
        'Invite teammates, manage roles, and keep consent operations in the right hands.',
    },
  },
  [ROUTES.SETTINGS_SYSTEM_SETTINGS]: {
    staticPath: `/${ROUTES.SETTINGS}/${SETTINGS_TAB_URLS[ROUTES.SETTINGS_SYSTEM_SETTINGS]}`,
    meta: {
      title: withSiteTitle(SETTINGS_TAB_LABELS[ROUTES.SETTINGS_SYSTEM_SETTINGS]),
      description: 'Handle system setting of CookieArmor. Such as how often to send scan emails.',
    },
  },
  [ROUTES.ACCEPT_INVITE]: {
    staticPath: `/${ROUTES.ACCEPT_INVITE}`,
    meta: {
      title: withSiteTitle('Accept Invitation'),
      description: 'Join your CookieArmor account and start managing consent with your team.',
    },
  },
  [ROUTES.EMAIL_VERIFIED]: {
    staticPath: `/${ROUTES.EMAIL_VERIFIED}`,
    meta: {
      // Neutral on purpose — this route also renders the already-verified,
      // invalid-link, and no-result states.
      title: withSiteTitle('Email Verification'),
      description: 'Confirm your email and secure access to your CookieArmor account.',
    },
  },
  [ROUTES.TERMS]: {
    staticPath: `/${ROUTES.TERMS}`,
    rewritePattern: `/${ROUTES.TERMS}(/[^/]+)?`,
    meta: {
      title: withSiteTitle('Terms of Usage'),
      description:
        'Read the CookieArmor Terms of Usage that govern access to the platform, consent banners, scans, and your account.',
    },
  },
}
